import type { InversedMapBounds, MapBounds, MapKeyGroup } from '#cmui/types/map'
import type { ExtendedMarker, MapPool, ZoneStation } from '~~/interfaces/map'
import useMapStore from '~~/app/store/map'
import type { LatLngTuple } from 'leaflet'

export default function useMap(
    defaultCenter: LatLngTuple = [48.580002, 7.75],
    networkId: number | undefined = undefined,
    city: string | undefined = undefined,
    countryId: number | undefined = undefined,
    canChargeWithChargemap: boolean | undefined = undefined
) {
    const config = useRuntimeConfig()

    const mapKeys: Ref<MapKeyGroup[] | undefined> = ref([])
    const bounds: Ref<InversedMapBounds | undefined> = ref(undefined)
    const markersData: Ref<any[]> = ref([]) // TODO: improve type
    const selectedMarker: Ref<ExtendedMarker | undefined> = ref(undefined)
    const markerList: Ref<ExtendedMarker[]> = ref([])
    const station: Ref<ZoneStation> = ref({} as ZoneStation)
    const poolInstance: Ref<MapPool | undefined> = ref(undefined)
    const timeout: Ref<null | number | NodeJS.Timeout> = ref(null)

    const mapCenter = computed<LatLngTuple | undefined>(() => {
        if (
            station.value?.locationInfo?.latitude &&
            station.value?.locationInfo?.longitude
        ) {
            return [
                Number(station.value?.locationInfo?.latitude),
                Number(station.value?.locationInfo?.longitude)
            ] as LatLngTuple
        }

        return defaultCenter || undefined
    })

    const buildMarkerArray = (items: any): ExtendedMarker[] => {
        if (!items) {
            return []
        }

        return items.map((loc: MapPool) => {
            if (loc.type === 'pool') {
                return {
                    id: loc.pool.id,
                    latLng: [
                        loc.pool.gpsCoordinates?.lat || loc.lat,
                        loc.pool.gpsCoordinates?.lon || loc.lng
                    ],
                    icon: `${config.public.LEGACY_URL}/medias/v2/map_icons/64x64/${loc.icon}`,
                    type: loc.type as 'pool',
                    slug: loc.pool.slug
                }
            }

            return {
                latLng: [loc.lat, loc.lng],
                id: loc.lat + loc.lng,
                icon: `${config.public.CHARGEMAP_URL}/client/chargemap/images/cluster.png`,
                count: loc.count,
                type: loc.type as 'cluster'
            }
        })
    }

    const getKeyData = () => {
        const { fetchKeyData } = useMapStore()

        fetchKeyData().then((keys: MapKeyGroup[]) => {
            if (!keys) return

            mapKeys.value = keys
        })
    }

    const getMapData = () => {
        // We select the station marker
        if (!selectedMarker.value && markerList.value.length !== 0) {
            selectedMarker.value = markerList.value.find(
                (marker) => marker.id === station.value.id
            )
            poolInstance.value = markersData.value.find(
                (markerData) => markerData?.pool?.id === station.value.id
            )
        }

        const { fetchMapMarkers } = useMapStore()

        if (bounds.value) {
            fetchMapMarkers(
                bounds.value,
                networkId,
                city,
                countryId,
                canChargeWithChargemap
            ).then((markers) => {
                if (markers) {
                    markersData.value = markers.items
                    markerList.value = buildMarkerArray(markers.items)
                }

                // We select the station marker
                if (!selectedMarker.value && markerList.value.length !== 0) {
                    selectedMarker.value = markerList.value.find(
                        (marker) => marker.id === station.value.id
                    )
                    poolInstance.value = markersData.value.find(
                        (markerData) =>
                            markerData?.pool?.id === station.value.id
                    )
                }
            })
        }
    }

    const updatedBounds = (
        _bounds: MapBounds,
        reversedBounds: InversedMapBounds
    ) => {
        bounds.value = reversedBounds

        clearTimeout(timeout.value as number)
        timeout.value = setTimeout(() => {
            getMapData()
        }, 50)
    }

    const chargemapURL = computed<string>(
        () => config.public.CHARGEMAP_URL || ''
    )

    const openTab = (url: string) => {
        if (station.value?.id) {
            return navigateTo(`${chargemapURL.value}/${url}`, {
                external: true,
                open: {
                    target: '_blank'
                }
            })
        }
    }

    return {
        // Data
        mapKeys,
        markersData,
        selectedMarker,
        markerList,
        bounds,
        station,
        mapCenter,

        // Methods
        getMapData,
        getKeyData,
        buildMarkerArray,
        updatedBounds,
        openTab
    }
}
